import { createSlice } from '@reduxjs/toolkit';
import { uniqBy } from 'lib';
import { contentInitialState } from './initialState/contentInitialState';
import {
  fetchContents,
  reportUnrelatedPost,
  updatePostSentiment,
  fetchTwitterAggregateInfo,
  markPostAsSeen,
} from './actions';

export const contentSlice = createSlice({
  name: 'contentSlice',
  initialState: contentInitialState,
  reducers: {
    applyCurrentPageOnReload: (state) => {
      state.onreloadContents += 1;
    },
    setPlatformCurrentPageNumber: (state, action) => {
      const { platform, pageNumber } = action.payload;
      state.contents[platform].pageNumber = pageNumber;
    },
    toggleAllSelectedContents: (state, action) => {
      const { isChecked, platform } = action.payload;
      if (isChecked) {
        state.selectedContents = state.contents[platform].data.map(
          (content) => content.id
        );
      } else {
        state.selectedContents = [];
      }
    },
    toggleSelectedContent: (state, action) => {
      const { isChecked, selectedContentId } = action.payload;
      if (isChecked) {
        state.selectedContents.push(selectedContentId);
      } else {
        state.selectedContents = state.selectedContents.filter(
          (contentId) => contentId !== selectedContentId
        );
      }
    },
    clearSelectedContents: (state) => {
      state.selectedContents = [];
    },
    updateContent: (state, action) => {
      const { newContent, platform, isMultiPlatform } = action.payload;
      const contents = isMultiPlatform
        ? state.contents['multiPlatform']
        : state.contents[platform];
      contents.data.forEach((content, index) => {
        if (content.id === newContent.id) {
          contents.data[index] = {
            ...contents.data[index],
            ...newContent,
          };
        }
      });
    },
    setResultSearchQuery: (state, action) => {
      const { resultSearchQuery, platform } = action.payload;
      state.resultSearchQuery[platform] = resultSearchQuery;
    },
    changeExpandedIndex: (state, action) => {
      state.expandedIndex =
        action.payload?.index ?? contentInitialState.expandedIndex;

      state.isExpanded = action.payload?.index !== null;
    },
    setIsUpOrDownKeyClicked: (state, action) => {
      state.isUpOrDownKeyClicked = action.payload;
    },
    setContentTags: (state, action) => {
      const {
        platform,
        tags,
        ids = [],
        isMultiPlatform,
        type = 'singleContent',
      } = action.payload;
      const contents = isMultiPlatform
        ? state.contents['multiPlatform']
        : state.contents[platform];

      contents.data.forEach((content, index) => {
        if (ids.includes(content.id)) {
          /**
           * اگر یک محتوا بود که تگ ها جایگزین میشن ولی اگر بصورت دسته جمعی بود
           * باید ارایه ای منحصر به فرد از تگهای قبلی و تگ های جدید داشته باشیم
           */
          contents.data[index].contentTags =
            type === 'singleContent'
              ? tags
              : uniqBy(contents.data[index].contentTags.concat(tags), 'id');
        }
      });
    },
    clearContents: (state) => {
      state.contents = contentInitialState.contents;
    },
    setCurrentContentId: (state, action) => {
      state.currentContentId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContents.pending, (state, action) => {
        const { platform, params } = action.meta.arg;
        const { track_total } = params || {};
        if (track_total === 1) {
          state.contents[platform].loadingCount = true;
        } else {
          state.contents[platform].loading = true;
        }
        state.contents[platform].error = null;
      })
      .addCase(fetchContents.rejected, (state, action) => {
        const { platform, params } = action.meta.arg;
        const { track_total } = params || {};
        if (track_total === 1) {
          state.contents[platform].loadingCount = false;
        } else {
          state.contents[platform].loading = false;
        }
        state.contents[platform].error = action.payload;
      })
      .addCase(fetchContents.fulfilled, (state, action) => {
        const { platform, params } = action.meta.arg;
        const { track_total } = params || {};
        const { contents = [], total, pageNumber } = action.payload;
        if (track_total === 1) {
          state.contents[platform].total = total;
          state.contents[platform].loadingCount = false;
        } else {
          state.contents[platform].data = contents;
          state.contents[platform].loading = false;
        }

        state.contents[platform].error = null;
        if (pageNumber) {
          state.contents[platform].pageNumber = pageNumber;
        }
      })

      .addCase(reportUnrelatedPost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reportUnrelatedPost.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;
        state.error = null;
      })
      .addCase(reportUnrelatedPost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePostSentiment.pending, (state) => {
        state.updatePostSentiment.error = null;
      })
      .addCase(updatePostSentiment.fulfilled, (state, action) => {
        const { params, frontPlatform } = action.meta.arg;
        const { post_id, value } = params;

        const items = state.contents[frontPlatform]?.data;
        const target = items?.find((item) => item.id === post_id);
        if (target) target.sentiment = value;

        state.updatePostSentiment.data = action.payload;
        state.updatePostSentiment.error = null;
      })
      .addCase(updatePostSentiment.rejected, (state, action) => {
        state.updatePostSentiment.error = action.payload;
      })
      .addCase(fetchTwitterAggregateInfo.pending, (state) => {
        state.twitterAggregateInfo.loading = true;
        state.twitterAggregateInfo.error = null;
      })
      .addCase(fetchTwitterAggregateInfo.fulfilled, (state, action) => {
        state.twitterAggregateInfo.data = action.payload;
        state.twitterAggregateInfo.loading = false;
        state.twitterAggregateInfo.error = null;
      })
      .addCase(fetchTwitterAggregateInfo.rejected, (state, action) => {
        state.twitterAggregateInfo.loading = false;
        state.twitterAggregateInfo.error = action.payload;
      })
      .addCase(markPostAsSeen.pending, (state) => {
        state.markPostAsSeen.loading = true;
        state.markPostAsSeen.error = null;
      })
      .addCase(markPostAsSeen.fulfilled, (state, action) => {
        const { postId } = action.meta.arg;

        Object.values(state.contents).forEach(({ data }) => {
          const item = data?.find((content) => content.id === postId);
          if (item) item.is_seen = true;
        });

        state.markPostAsSeen.loading = false;
        state.markPostAsSeen.error = null;
      })
      .addCase(markPostAsSeen.rejected, (state, action) => {
        state.markPostAsSeen.loading = false;
        state.markPostAsSeen.error = action.payload;
      });
  },
});

export const {
  setPlatformCurrentPageNumber,
  toggleAllSelectedContents,
  toggleSelectedContent,
  clearSelectedContents,
  updateContent,
  setResultSearchQuery,
  changeExpandedIndex,
  setIsUpOrDownKeyClicked,
  setContentTags,
  clearContents,
  setCurrentContentId,
  applyCurrentPageOnReload,
} = contentSlice.actions;
